import React from "react";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, CarbonProgramOutcomesResponse, FieldPracticeResponse, VIEW_TYPE } from "models";

import { FlexPageSpin, HideFeature } from "components";

import { CollapsibleImpactPanel } from "pages/app/all-field-view/components/CollapsibleImpactPanel";
import { InsightsImpactPanel } from "pages/app/field-timeline/components/InsightsImpactPanel";
import { InterventionImpactPanel } from "pages/app/field-timeline/components/InterventionImpactPanel";
import { InventoryImpactPanel } from "pages/app/field-timeline/components/InventoryImpactPanel";
import { SoilPlus3ImpactPanel } from "pages/app/field-timeline/components/SoilPlus3ImpactPanel";

// same panel-selection logic AllFieldView/Timeline uses for its Impact Panel (CollapsibleImpactPanel + one of 4
// concrete panels depending on program/feature flags), reused here verbatim so the two pages present identical
// program progress/payout information — this page just drives it from its own already-fetched data.
export type AllFieldsImpactPanelProps = {
  isHidden: boolean;
  isLoading: boolean;
  className?: string;
  reportingYear: number;
  farmId: string;
  carbonProgram: CarbonProgram | null | undefined;
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | undefined;
  progress: number;
  fieldSizes: Record<string, number>;
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined;
  isFetchingOutcomes: boolean;
  isFetchingPractices: boolean;
  enrolledFieldsCount: number;
  shouldShowInsightsImpactPanel: boolean;
  shouldShowSoilPlus3ImpactPanel: boolean | null;
  shouldShowSubmissionSuccessModalContent: boolean;
  onOpenBulkEnrollDrawer: () => void;
  onNavigateToMissingData: () => void;
};

export const AllFieldsImpactPanel = ({
  isHidden,
  isLoading,
  className,
  reportingYear,
  farmId,
  carbonProgram,
  carbonProgramOutcomes,
  progress,
  fieldSizes,
  allFieldsPractices,
  isFetchingOutcomes,
  isFetchingPractices,
  enrolledFieldsCount,
  shouldShowInsightsImpactPanel,
  shouldShowSoilPlus3ImpactPanel,
  shouldShowSubmissionSuccessModalContent,
  onOpenBulkEnrollDrawer,
  onNavigateToMissingData,
}: AllFieldsImpactPanelProps) => (
  <HideFeature hide={isHidden} fallback={null}>
    <CollapsibleImpactPanel className={className}>
      {isLoading ? (
        <FlexPageSpin />
      ) : (
        <>
          {shouldShowInsightsImpactPanel ? (
            <InsightsImpactPanel
              reportingYear={reportingYear}
              farmId={farmId}
              carbonProgramOutcomes={carbonProgramOutcomes}
              fieldSizes={fieldSizes}
              allFieldsPractices={allFieldsPractices ?? {}}
              carbonProgram={carbonProgram}
              isFetchingOutcomes={isFetchingOutcomes}
              isFetchingPractices={isFetchingPractices}
            />
          ) : carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? (
            <InventoryImpactPanel
              carbonProgramOutcomes={carbonProgramOutcomes}
              progress={progress}
              fieldSizes={fieldSizes}
              shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
              hidePayouts={!carbonProgram.platformFeatures.fieldManagement?.wallet}
            />
          ) : shouldShowSoilPlus3ImpactPanel ? (
            <SoilPlus3ImpactPanel
              carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
              enrolledFieldsCount={enrolledFieldsCount}
              fieldSizes={fieldSizes}
              progress={progress}
              shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
              onOpenBulkEnrollDrawer={onOpenBulkEnrollDrawer}
              setNavigateToMissingData={onNavigateToMissingData}
            />
          ) : (
            <InterventionImpactPanel
              carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
              progress={progress}
              fieldSizes={fieldSizes}
              viewType={VIEW_TYPE.ALL_FIELDS}
              shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
              setNavigateToMissingData={onNavigateToMissingData}
            />
          )}
        </>
      )}
    </CollapsibleImpactPanel>
  </HideFeature>
);
